import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { serialize } from 'object-to-formdata';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { AvatarAdapter } from '../../adapters/avatar.adapter';
import { urlToFile } from '../../functions/urlToFile';
import {
  deleteCompanyLogo,
  deleteCompanyLogoFailure,
  deleteCompanyLogoSuccess,
  loadCompanyLogo,
  loadCompanyLogoFailure,
  loadCompanyLogoSuccess,
  updateCompanyLogo,
  updateCompanyLogoFailure,
  updateCompanyLogoSuccess,
} from './company-logo.actions';

@Injectable()
export class CompanyLogoEffects {
  private readonly actions$ = inject(Actions);
  private readonly avatarAdapter = inject(AvatarAdapter);
  private readonly toastsService = inject(ToastsService);
  
  loadCompanyLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanyLogo),
      exhaustMap(() =>
        this.avatarAdapter.getCompanyAvatar().pipe(
          map((result) => loadCompanyLogoSuccess(result)),
          catchError((error) => {
            return of(loadCompanyLogoFailure({ error }));
          })
        )
      )
    )
  );

  updateCompanyLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCompanyLogo),
      concatMap(async (props) => serialize({ image: await urlToFile(props.payload, 'image.png', 'image/png') })),
      mergeMap((data) =>
        this.avatarAdapter.editCompanyAvatar(data).pipe(
          map((result) => updateCompanyLogoSuccess(result)),
          catchError((error) => {
            return of(updateCompanyLogoFailure({ error }));
          })
        )
      )
    )
  );

  updateCompanyLogoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateCompanyLogoSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.design.toasts.logo-updated');
        })
      ),
    { dispatch: false }
  );

  deleteCompanyLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCompanyLogo),
      mergeMap((props) =>
        this.avatarAdapter.deleteCompanyAvatar().pipe(
          map(() => deleteCompanyLogoSuccess()),
          catchError((error) => {
            return of(deleteCompanyLogoFailure({ error }));
          })
        )
      )
    )
  );

  deleteCompanyLogoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteCompanyLogoSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.design.toasts.logo-deleted');
        })
      ),
    { dispatch: false }
  );

  loadCompanyLogoFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCompanyLogoFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );
}
